import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';

const state = {
  connection: null,
  inactivityThreshold: 60000,

  lockResolver: null,
};

const getters = {
  origin: () => process.env.VUE_APP_SPACEIN_API_BROKER_URL,
  pathPrefix: () => '/broker-api',

  connection: (state) => {
    return state.connection;
  },
  isConnected: (state) => {
    return state.connection?.connectionState !== 'Disconnected'
  },

  lockResolver: (state) => state.lockResolver,
}

const mutations = {
  CONNECTION(state, data) {
    state.connection = data;
  },

  LOCK_RESOLVER: (state, data) => state.lockResolver = data,
}

const actions = {
  init({ getters, rootGetters, commit, dispatch }) {
    // Setup connection
    const build = new HubConnectionBuilder()
      .withUrl(`${getters.origin}${getters.pathPrefix}/connect`, {
        accessTokenFactory: async () => await dispatch('api/auth/getAccessToken', 'connection builder', { root: true })
      })
      .configureLogging(LogLevel.Information)
      .build();
    // init connection if it doesn't already exist
    build.logging = true;

    commit('CONNECTION', build);
    let connection = getters.connection;

    connection.on('DispatchEvent', async (message) => {
      dispatch('api/messageQueue/handleConnectionMessage', message, { root: true })
    });

    connection.onclose(async () => {
      if (!rootGetters['api/auth/forcedShutdown']) {
        console.log('connection closed');
        await dispatch('reconnect')
      } else {
        console.log('Connection closed because the user was unauthenticated');
      }
    });

    let connectionChecks = 0;

    setInterval(() => {
      if (connection && !getters.isConnected) {
        console.log('Connection failed.');
        connectionChecks++;
        if (connectionChecks > 1) {
          dispatch('reconnect')
        }
      } else {
        console.log('connection fine');
        connectionChecks = 0;
      }
    }, 3000);

    return
  },
  async closeConnection({ getters, commit }) {
    let connection = getters.connection;
    if (connection !== null) {
      console.log('closing the connection');
      connection.stop();
      commit('CONNECTION', connection);
    }
  },
  async closeConnectionRemoveUser({ commit, dispatch }) {
    commit('api/auth/API_USER', null, { root: true });
    dispatch('closeConnection');
  },
  async forceCloseConnection({ commit, dispatch }) {
    commit('api/auth/FORCED_SHUTDOWN', true, { root: true });
    dispatch('closeConnection');
  },

  async connect({ getters, rootGetters, commit, dispatch }) {
    console.log('Connecting')
    let connection = getters.connection

    if (!getters.isConnected) {
      console.log('Connection doesn\'t exists')
      try {
        console.log('Connection start')
        await connection.start()
        console.log('Connection started')
        commit('CONNECTION', connection);
        if (rootGetters['loading/isFirstLoad']) return
        //re-enter user into their current location again (from url)
        let currentRouteData = rootGetters['routing/routeData']
        if (!rootGetters.getIfCurrentUsersRoomIsHub) {
          console.log('Enter area')
          let res = await dispatch('api/nav/joinRoom', rootGetters.getCurrentUsersRoomId, { root: true })
          if (!res?.success) dispatch('routing/goToCurrentArea', null, { root: true })
          if (rootGetters['routing/routeName'] === 'area') {
            await dispatch('routing/changeHandler', [currentRouteData], { root: true })
          }
        } else {
          console.log('Enter whatever')
          await dispatch('routing/changeHandler', [currentRouteData], { root: true })
        }
      } catch (error) {
        console.log('reconnect error', error)
        if (error.message && error.message === 'Unauthorized') {
          console.log('Error connecting to client broker: Unauthorised')
          dispatch('api/auth/setUnauthorisedStatus', null, { root: true });
        } else {
          console.log('Error connecting to client broker: unknown reason')
        }
        console.log('Try re-connecting in 3 s');
        setTimeout(() => dispatch('reconnect'), 3000);
        return
      }
    } else {
      console.log('Connection exists')
    }
  },
  async reconnect({ dispatch }) {
    console.log('Try re-connecting in 500 ms');
    setTimeout(async () => {
      let isLoggedIn = await dispatch('api/auth/getIsLoggedIn', null, { root: true })
      if (!isLoggedIn) {
        console.log('Authorise and then re-connect');
        dispatch('api/auth/authenticateUser', () => dispatch('connect'), { root: true });
      } else {
        console.log('Re-connect');
        dispatch('connect');
      }
    }, 500)
  },

  //blur/focus events
  initWindowEvents({ getters, rootGetters, commit, dispatch }) {
    //inactivity - check connection
    const focusEvent = async () => {
      if (!getters.isConnected) {
        console.log('focus')
        await dispatch('reconnect')
      }
    }
    window.removeEventListener('focus', focusEvent, true);
    window.addEventListener('focus', focusEvent, true);

    //before unload
    const unloadEvent = async () => {
      if (rootGetters.getCurrentAreaId) await dispatch('analytics/leaveSpace', 'direct', { root: true })
      await dispatch('api/auth/logoutGuestOnDisconnect', null, { root: true })
    }
    window.removeEventListener('beforeunload', unloadEvent, { capture: true })
    window.addEventListener('beforeunload', unloadEvent, { capture: true })

    //web lock to prevent disconnect/tab sleep
    let lockResolver;
    if (navigator?.locks?.request) {
      const promise = new Promise((res) => {
        lockResolver = res
        commit('LOCK_RESOLVER', lockResolver)
      })

      navigator.locks.request('spacein_lock', { mode: "shared" }, () => {
        return promise
      })
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
